import type React from "react"
import { useEffect, useRef, useState } from "react"

interface Props {
	tooltipLabel: string
	/** Trigger glyph; defaults to the hamburger so existing callers are unchanged. */
	triggerIcon?: React.ReactNode
	items: Array<{
		label: string
		onSelect: () => void
		/** Render in the destructive (red) tone — e.g. a Delete action. */
		destructive?: boolean
	}>
}

const HamburgerIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 4h10" />
		<path d="M3 8h10" />
		<path d="M3 12h10" />
	</svg>
)

/** Vertical 3-dot (kebab) trigger glyph — pass via `triggerIcon`. */
export const KebabIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="currentColor"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<circle cx="8" cy="3" r="1.4" />
		<circle cx="8" cy="8" r="1.4" />
		<circle cx="8" cy="13" r="1.4" />
	</svg>
)

export function ManageMenu({
	tooltipLabel,
	items,
	triggerIcon = <HamburgerIcon />,
}: Props) {
	const [open, setOpen] = useState(false)
	const wrapperRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handleDown = (e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("mousedown", handleDown)
		window.addEventListener("keydown", handleKey)
		return () => {
			window.removeEventListener("mousedown", handleDown)
			window.removeEventListener("keydown", handleKey)
		}
	}, [open])

	return (
		<div ref={wrapperRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-label={tooltipLabel}
				title={tooltipLabel}
				aria-haspopup="menu"
				aria-expanded={open}
				className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
			>
				{triggerIcon}
			</button>
			{open ? (
				<div
					role="menu"
					className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 py-1"
				>
					{items.length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground/80">—</div>
					) : (
						items.map((item) => (
							<button
								key={item.label}
								type="button"
								role="menuitem"
								onClick={() => {
									setOpen(false)
									item.onSelect()
								}}
								className={`w-full text-left px-3 py-2 text-sm hover:bg-accent ${
									item.destructive
										? "text-destructive hover:text-destructive"
										: "text-foreground/80 hover:text-foreground"
								}`}
							>
								{item.label}
							</button>
						))
					)}
				</div>
			) : null}
		</div>
	)
}
